// `.inp` section readers for Kinematic Wave/Steady Flow routing —
// invert elevations, conduits, storage nodes, orifices/pumps/weirs/outlets
// (post-filtered to their STORAGE-node-only KW/STEADY scope) and dividers.
// Extracted out of routing.ts's own simulateNetworkRouting, matching this
// codebase's existing structureReaders.ts/conduitReaders.ts convention of
// keeping `.inp` parsing separate from the routing loop itself.
//
// `readStorageOrifices`/`readStoragePumps`/`readStorageWeirs` below are
// named distinctly (not `readOrifices`/`readPumps`/`readWeirs`, despite
// wrapping those exact functions from structureReaders.ts, imported here as
// `readAllOrifices`/`readAllPumps`/`readAllWeirs`) so importing both this
// file and structureReaders.ts together never needs manual aliasing to
// avoid a same-named-but-differently-scoped collision — a real landmine a
// future editor could otherwise silently import the wrong one of.

import { readRawCurves, type ConvertedCurve, type RawCurve } from "../curves.ts";
import { parseConduitRows } from "../structures/conduitReaders.ts";
import { conduitGeometryFromInp, type ConduitGeometry } from "./kinematicWave.ts";
import {
  setupFunctionalOutlet,
  setupTabularOutlet,
  type OutletSetup,
} from "../structures/outlet.ts";
import { type ConduitLossSetup } from "../structures/conduitLosses.ts";
import {
  setupCutoffDivider,
  setupTabularDivider,
  setupWeirDivider,
  type DividerSetup,
} from "../structures/divider.ts";
import {
  readOrifices as readAllOrifices,
  readPumps as readAllPumps,
  readWeirs as readAllWeirs,
  type OrificeEdge,
  type PumpEdge,
  type WeirEdge,
} from "../structures/structureReaders.ts";
import { readStorageCurvesByNode, type StorageCurve } from "../storage.ts";
import { warningText } from "../engineWarnings.ts";
import {
  isLinkOffsetStar,
  readLinkOffsetConvention,
  resolveLinkOffsetFt,
  toCfs,
  toFeet,
  type Feet,
  type UnitSystemName,
} from "../../model/units.ts";

// invertElev/StorageNode.initDepth keep the `Feet` brand end-to-end (map
// value, not just the `toFeet()` call site) since they're the two values
// every KW/STEADY structure reader below reads back out of a collection —
// exactly the crossing the brand exists to guard. Most of this file's other
// `toFeet`/`toCfs` results still land in plain `number` struct fields
// (`ConduitEdge.lengthFt`, `OutletSetup`, ...); re-branding all of those too
// would ripple into `conduitReaders.ts`/`structureReaders.ts`/
// `dynamicWaveRouting.ts` and beyond for little practical gain, since a
// wrong-but-plausible raw `.inp` value already gets caught by unit tests on
// the individual `toFeet`/`toCfs` conversions themselves.
export function readInvertElevations(
  raw: ReadonlyMap<string, string[][]>,
  sys: UnitSystemName
): Map<string, Feet> {
  const elevations = new Map<string, Feet>();
  for (const section of ["JUNCTIONS", "OUTFALLS", "STORAGE", "DIVIDERS"]) {
    for (const row of raw.get(section) ?? []) {
      if (row[0] && row[1] !== undefined) elevations.set(row[0], toFeet(Number(row[1]), sys));
    }
  }
  return elevations;
}

/** [JUNCTIONS] MaxDepth only, used solely for the `[CONTROLS]` NODE MAXDEPTH premise — STORAGE/DIVIDER nodes are left at 0. */
export function readNodeMaxDepthFt(
  raw: ReadonlyMap<string, string[][]>,
  sys: UnitSystemName
): Map<string, Feet> {
  const result = new Map<string, Feet>();
  for (const row of raw.get("JUNCTIONS") ?? []) {
    if (row[0] && row[2] !== undefined) result.set(row[0], toFeet(Number(row[2]), sys));
  }
  return result;
}

export interface ConduitEdge {
  kind: "conduit";
  name: string;
  fromNode: string;
  toNode: string;
  geometry: ConduitGeometry;
  /** Inlet/Outlet Offset, already resolved to a plain depth-above-invert via `[OPTIONS] LINK_OFFSETS`. */
  offset1: number;
  offset2: number;
  /** [XSECTIONS] Barrels (parallel identical conduits, default 1) — `geometry`/`ConduitState` stay per-barrel internally, so the caller divides inflow by this before `stepConduit` and multiplies the returned outflow back. */
  barrels: number;
  /** ft — duplicated from `geometry` so `[CONTROLS]`' CONDUIT LENGTH premise has a readback value without re-deriving it. */
  lengthFt: number;
  /** Fraction (not percent) — same duplication reasoning as `lengthFt`, for CONDUIT SLOPE. */
  slope: number;
  /** Only SeepRate is ever read for KINWAVE/STEADY (Kentry/Kexit/Kavg/FlapGate are DW-only) — `NO_CONDUIT_LOSSES` when this conduit has no `[LOSSES]` row. */
  losses: ConduitLossSetup;
}

export interface OutletEdge {
  kind: "outlet";
  name: string;
  fromNode: string;
  toNode: string;
  setup: OutletSetup;
}

export interface StorageNode {
  curve: StorageCurve;
  initDepth: Feet;
}

export function readConduits(
  raw: ReadonlyMap<string, string[][]>,
  invertElev: Map<string, number>,
  sys: UnitSystemName,
  warnings: Set<string> = new Set()
): ConduitEdge[] {
  return parseConduitRows(raw, invertElev, sys, warnings).map((row) => ({
    kind: "conduit",
    name: row.name,
    fromNode: row.fromNode,
    toNode: row.toNode,
    geometry: conduitGeometryFromInp(
      row.xsect,
      row.lengthFt,
      row.roughnessOverride ?? row.manningN,
      row.slope
    ),
    offset1: row.offset1,
    offset2: row.offset2,
    barrels: row.barrels,
    lengthFt: row.lengthFt,
    slope: row.slope,
    losses: row.losses,
  }));
}

/**
 * Every conduit's own full-flow capacity, independent of which routing
 * method actually ran — `simulateProject.ts`'s post-hoc `linkCapacity`
 * series needs this even under DYNWAVE, whose own conduit list isn't
 * otherwise exposed outside `dynamicWaveRouting.ts`. Cheap to rebuild:
 * `readConduits` is a pure function of the same `[CONDUITS]`/`[XSECTIONS]`/
 * `[STORAGE]` geometry either routing method derives `qFull` from.
 */
export function readConduitFullFlows(
  raw: ReadonlyMap<string, string[][]>,
  sys: UnitSystemName
): Map<string, number> {
  const invertElev = readInvertElevations(raw, sys);
  const conduits = readConduits(raw, invertElev, sys);
  const result = new Map<string, number>();
  for (const c of conduits) result.set(c.name, c.geometry.qFull);
  return result;
}

/** Adds `initDepth` (a KW/STEADY-specific column) on top of `readStorageCurvesByNode`'s shared curve. */
export function readStorageNodes(
  raw: ReadonlyMap<string, string[][]>,
  sys: UnitSystemName,
  warnings: Set<string> = new Set()
): Map<string, StorageNode> {
  const curves = readStorageCurvesByNode(raw, sys, warnings);
  const nodes = new Map<string, StorageNode>();
  for (const row of raw.get("STORAGE") ?? []) {
    const [name, , , initDepth] = row;
    if (!name) continue;
    const curve = curves.get(name);
    if (!curve) continue; // already warned by readStorageCurvesByNode
    nodes.set(name, {
      curve,
      initDepth: toFeet(Number(initDepth ?? "0"), sys),
    });
  }
  return nodes;
}

/** Every [ORIFICES] row sourced from a STORAGE node — `structureReaders.ts`'s own `readOrifices` parses every row regardless of source; the storage restriction is applied here as a post-filter. Named distinctly (not `readOrifices` too) from that same-shaped sibling — see this file's own header for why. */
export function readStorageOrifices(
  raw: ReadonlyMap<string, string[][]>,
  storageNodes: Map<string, StorageNode>,
  sys: UnitSystemName,
  warnings: Set<string>,
  invertElev: ReadonlyMap<string, number>
): OrificeEdge[] {
  return readAllOrifices(raw, sys, warnings, invertElev).filter((e) =>
    storageNodes.has(e.fromNode)
  );
}

/**
 * Every [PUMPS] row sourced from a STORAGE node (post-filter, as above).
 * PUMP3/PUMP5 curves need the downstream node's own head; `nodeOldDepth`
 * (the previous step's converged depth) is the correct value to read there,
 * since the real engine also doesn't refresh a non-storage node's depth
 * mid-step until every link has been routed.
 */
export function readStoragePumps(
  raw: ReadonlyMap<string, string[][]>,
  storageNodes: Map<string, StorageNode>,
  curves: Map<string, ConvertedCurve>,
  sys: UnitSystemName,
  warnings: Set<string>
): PumpEdge[] {
  return readAllPumps(raw, curves, sys, warnings).filter((e) => storageNodes.has(e.fromNode));
}

/** Every [WEIRS] row sourced from a STORAGE node (post-filter, as above). `rawCurves` is deliberately the raw/unconverted discharge-coefficient curve map, not `readCurves`'s flow-converted one. */
export function readStorageWeirs(
  raw: ReadonlyMap<string, string[][]>,
  storageNodes: Map<string, StorageNode>,
  sys: UnitSystemName,
  rawCurves: Map<string, RawCurve>,
  warnings: Set<string>,
  invertElev: ReadonlyMap<string, number>
): WeirEdge[] {
  return readAllWeirs(raw, sys, rawCurves, warnings, invertElev).filter((e) =>
    storageNodes.has(e.fromNode)
  );
}

/**
 * Every [OUTLETS] row sourced from a STORAGE node — FUNCTIONAL/DEPTH or
 * TABULAR/DEPTH relation only (NODE_HEAD variants only differ under Dynamic
 * Wave). The FUNCTIONAL relation's `a`/`b` coefficients are read raw,
 * unconverted, matching the real engine's own `outlet_readParams`.
 */
export function readOutlets(
  raw: ReadonlyMap<string, string[][]>,
  storageNodes: Map<string, StorageNode>,
  curves: Map<string, ConvertedCurve>,
  sys: UnitSystemName,
  flowUnits: string,
  warnings: Set<string> = new Set(),
  invertElev: ReadonlyMap<string, number> = new Map()
): OutletEdge[] {
  const linkOffsetConvention = readLinkOffsetConvention(raw);
  const result: OutletEdge[] = [];
  for (const row of raw.get("OUTLETS") ?? []) {
    // FlapGate sits at a different column depending on the relation type:
    // right after Coeff+Expon for FUNCTIONAL (index 7), or right after the
    // curve name for TABULAR (index 6 — the same slot `b` destructures,
    // unused there since TABULAR needs no second numeric parameter).
    const [name, fromNode, toNode, crestHeight, relation, a, b, flapGateColFunctional] = row;
    if (!name || !fromNode || !toNode) {
      if (name) {
        warnings.add(
          warningText(
            "engineWarning.tooFewColumns",
            `Outlet "${name}" has too few columns to resolve its own end nodes and is not modeled.`,
            { kind: "Outlet", name, what: "end nodes" }
          )
        );
      }
      continue;
    }
    // Deliberate restriction, not a malformed row: KW/STEADY only models an
    // [OUTLETS] row sourced from a STORAGE node, same as the other
    // structures — no warning here either.
    if (!storageNodes.has(fromNode)) continue;
    const isFunctional = (relation ?? "").toUpperCase().startsWith("FUNCTIONAL");
    const hasFlapGate = (isFunctional ? flapGateColFunctional : b)?.toUpperCase() === "YES";
    const nodeInvertFt = invertElev.get(fromNode) ?? 0;
    const crestOffsetFt = (
      isLinkOffsetStar(crestHeight, linkOffsetConvention)
        ? 0
        : Math.max(
            0,
            resolveLinkOffsetFt(
              toFeet(Number(crestHeight), sys),
              nodeInvertFt,
              linkOffsetConvention
            )
          )
    ) as Feet;
    if (isFunctional) {
      result.push({
        kind: "outlet",
        name,
        fromNode,
        toNode,
        setup: setupFunctionalOutlet(
          crestOffsetFt,
          Number(a),
          Number(b),
          sys,
          flowUnits,
          "depth",
          hasFlapGate
        ),
      });
    } else {
      const curve = curves.get(a);
      if (!curve) {
        warnings.add(
          warningText(
            "engineWarning.outletUndefinedCurve",
            `Outlet "${name}" is not modeled: its own TABULAR curve "${a}" is undefined.`,
            { name, curve: a }
          )
        );
        continue;
      }
      result.push({
        kind: "outlet",
        name,
        fromNode,
        toNode,
        setup: setupTabularOutlet(crestOffsetFt, curve, "depth", hasFlapGate),
      });
    }
  }
  return result;
}

export interface DividerNode {
  /** OVERFLOW (no calibration params) is handled as a special case in the main per-node loop instead of by `dividerFlows`, since its diverted flow depends on the main link's realized outflow, not just the node's total inflow. */
  setup: DividerSetup | { kind: "OVERFLOW" };
  /** The OTHER outgoing conduit is the main line. */
  divertedLinkName: string;
}

/**
 * Every [DIVIDERS] row of CUTOFF/WEIR/TABULAR/OVERFLOW type. Column order:
 *   Name Elev DivertedLink CUTOFF   qCutoff            [MaxDepth ...]
 *   Name Elev DivertedLink WEIR     qMin  dhMax  cWeir  [MaxDepth ...]
 *   Name Elev DivertedLink TABULAR  curveID            [MaxDepth ...]
 * The TABULAR curve is looked up from the raw, unconverted curve map.
 */
export function readDividers(
  raw: ReadonlyMap<string, string[][]>,
  flowUnits: string,
  warnings: Set<string> = new Set()
): Map<string, DividerNode> {
  const rawCurves = readRawCurves(raw);
  // A plain name lookup, not a full readConduits() (which needs
  // invertElev/sys this function doesn't have) — the real engine's own
  // project_findObject(LINK, ...) is exactly this: does the name resolve to
  // SOME real link, nothing more.
  const conduitNames = new Set((raw.get("CONDUITS") ?? []).map((row) => row[0]));
  const result = new Map<string, DividerNode>();
  for (const row of raw.get("DIVIDERS") ?? []) {
    const [name, , divertedLinkName, type, param1, param2, param3] = row;
    if (!name || !divertedLinkName) {
      if (name) {
        warnings.add(
          warningText(
            "engineWarning.tooFewColumns",
            `Divider "${name}" has too few columns to resolve its own diverted link and is not modeled.`,
            { kind: "Divider", name, what: "diverted link" }
          )
        );
      }
      continue;
    }
    // The real engine's own ERR_NAME (project_findObject(LINK, tok[2]))
    // aborts the whole run on a misspelled diverted link — this port warns
    // and drops the divider instead (matching every other malformed-row
    // case here), rather than silently sending all of the node's inflow
    // through the "main" line and quietly discarding the diverted share
    // (dividerFlows'/OVERFLOW's own edge lookup never matches, so nothing
    // downstream of a bad name ever receives that flow).
    if (!conduitNames.has(divertedLinkName)) {
      warnings.add(
        warningText(
          "engineWarning.dividerUnknownLink",
          `Divider "${name}" is not modeled: its diverted link "${divertedLinkName}" is not a modeled conduit.`,
          { name, link: divertedLinkName }
        )
      );
      continue;
    }
    const typeUpper = (type ?? "").toUpperCase();

    let setup: DividerSetup | { kind: "OVERFLOW" } | undefined;
    if (typeUpper === "CUTOFF") {
      setup = setupCutoffDivider(toCfs(Number(param1), flowUnits));
    } else if (typeUpper === "WEIR") {
      setup = setupWeirDivider(Number(param1), Number(param2), Number(param3), flowUnits);
    } else if (typeUpper === "TABULAR") {
      const curve = rawCurves.get(param1 ?? "");
      if (curve) setup = setupTabularDivider(curve, flowUnits);
    } else if (typeUpper === "OVERFLOW") {
      setup = { kind: "OVERFLOW" };
    }

    if (!setup) {
      warnings.add(
        warningText(
          "engineWarning.dividerUnsupportedType",
          `Divider "${name}" is not modeled: only CUTOFF, WEIR, TABULAR and OVERFLOW divider types are supported (found "${type ?? ""}").`,
          { name, type: type ?? "" }
        )
      );
      continue;
    }
    result.set(name, { setup, divertedLinkName });
  }
  return result;
}
